'use client';
import { useTranslations } from 'next-intl';
import type { ComponentProps } from 'react';
import { DiscoverEntryBanner } from '../../DiscoverEntryBanner/DiscoverEntryBanner';

type QuickSeatConfig = {
  name: string;
  href: string;
  background: string;
};

type QuickSeatGameProps = Omit<ComponentProps<typeof DiscoverEntryBanner>, 'href' | 'image' | 'gameName' | 'title' | 'playButtonText'> & {
  config: QuickSeatConfig;
};

/**
 * Quick Seat entry banner for Live Casino Discover Page. Uses {@link DiscoverEntryBanner} for layout.
 * @property {Object} config - The configuration object for the game.
 * @property {string} config.name - The name of the game.
 * @property {string} config.href - The URL to navigate to the game.
 * @property {string} config.background - The background image URL for the game.
 * @property {string} [className] - Optional additional class names for the component.
 * @returns {JSX.Element} - The QuickSeatGame component.
 */
function QuickSeatGame({ className, config, ...props }: QuickSeatGameProps) {
  const t = useTranslations('live_casino.discover_page.quick_seat');
  const { name, href, background } = config;

  return (
    <DiscoverEntryBanner
      className={className}
      href={href}
      image={background}
      gameName={name}
      title={t.raw('title')}
      label={t('label')}
      playButtonText={t('play_game')}
      data-testid="quick-seat"
      data-track-container="Quick Seat"
      data-track-name="Quick Seat"
      {...props}
    />
  );
}

export { QuickSeatGame };
